import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { Task } from "@common/types/task";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { DatePicker } from "@design-system/DatePicker/DatePicker";
import { useUpdateTask } from "@api/task.api";
import { EditableCellContent } from "@design-system/ViewTable/EditableCellContent";
import { fullDateFullYearFormat } from "@common/helpers/date";
import { formatDateOnly } from "@helpers/date";
import { memo } from "react";
import { CaptureClicks } from "@design-system/CaptureClicks/CaptureClicks";
import { NestedTaskRow } from "../../types";
import { GroupDueDateSummary } from "./TaskGroupSummaryCells";

const TaskDueDateCell = memo(
    ({ row }: CellContext<ViewTableFeatures, NestedTaskRow, Date | null>) => {
        const task = row.original as Task;
        const updateTask = useUpdateTask(task);

        if (row.original.isNewPlaceholder) {
            return null;
        }

        const dateOnlyValue = task.dueDate ?? undefined;
        const formattedValue = dateOnlyValue
            ? formatDateOnly(dateOnlyValue, fullDateFullYearFormat)
            : undefined;

        return (
            <ViewTableCell>
                <div>
                    <CaptureClicks>
                        <DatePicker
                            mode="day"
                            variant="panel-input"
                            clearable
                            showTriggerIcon={false}
                            displayFormat={fullDateFullYearFormat}
                            placeholder="Add due date"
                            value={dateOnlyValue}
                            onValueChange={(value) =>
                                updateTask.mutate({ dueDate: value ?? null })
                            }
                            trigger={
                                <EditableCellContent
                                    isEmpty={!task.dueDate}
                                    addText="Add due date"
                                    hideIcon
                                >
                                    {formattedValue}
                                </EditableCellContent>
                            }
                        />
                    </CaptureClicks>
                </div>
            </ViewTableCell>
        );
    },
);

export const DueDateCell = (
    props: CellContext<ViewTableFeatures, NestedTaskRow, Date | null>,
) =>
    props.row.original.isGroup ? (
        <GroupDueDateSummary {...props} />
    ) : (
        <TaskDueDateCell {...props} />
    );
